<template>
    <div class="store-locator__content">
        <div class="store-locator__panel">
            <div class="store-locator__filter">
                <div class="umbraco-forms-container">
                    <div class="umbraco-forms-field dropdown">
                        <label for="storeSelectedCountry" class="umbraco-forms-label">{{ $t('Dictionary.StoreLocator.Country') }} *</label>
                        <div class="umbraco-forms-field-wrapper">
                            <select id="storeSelectedCountry" v-model="selectedCountry" name="storeSelectedCountry" @change="countryUpdated">
                                <option value="">
                                    {{ $t('Dictionary.StoreLocator.CountryPlaceholder') }}
                                </option>
                                <option v-for="country in countries" :key="country.code" :value="country.code">
                                    {{ country.label }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="umbraco-forms-field">
                        <label for="storeSearchTerm" class="umbraco-forms-label">{{ $t('Dictionary.StoreLocator.Search') }}</label>
                        <div class="umbraco-forms-field-wrapper">
                            <input id="storeSearchTerm" type="text" name="storeSearchTerm" class="text" :placeholder="$t('Dictionary.StoreLocator.SearchPlaceholder')" @input="searchTermUpdated($event.target.value)">
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="visibleStores && visibleStores.length" class="store-locator__list">
                <div class="store-locator__list-inner">
                    <div v-for="(store, index) in visibleStores" :key="index" class="store-locator__item">
                        <strong class="store-locator__item-title">{{ store.shopName }}</strong>
                        <span class="store-locator__item-address">{{ formatStoreAddress(store) }}</span>
                        <a :href="googleMapsUrl(store)" target="_blank" :title="$t('Dictionary.StoreLocator.GetDirections')" class="store-locator__item-link">{{ $t('Dictionary.StoreLocator.GetDirections') }}</a>
                    </div>
                </div>
            </div>
            <div v-else-if="!isLoading && !errorMessage" class="store-locator__no-results">
                <template v-if="selectedCountry">
                    {{ $t('Dictionary.StoreLocator.NoResults') }}
                </template>
                <template v-else-if="searchTerm">
                    {{ $t('Dictionary.StoreLocator.CountryRequired') }}
                </template>
            </div>
            <div v-if="errorMessage" class="store-locator__error">
                {{ errorMessage }}
            </div>
        </div>
        <div class="store-locator__map">
            <GoogleMap class-name="store-locator__map-inner" :api-key="apiKey" :map-id="mapId" :markers="filteredStores" :center="mapCenter" @map-ready="onMapReady" />
            <div class="spinner-wrap spinner-wrap--absolute" :aria-hidden="isLoading ? 'false' : 'true'">
                <div class="spinner"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { useStores } from '../composables/useStores';
import apiService from '../../api';
import GoogleMap from '../components/google-map.vue';

export default {
    name: 'StoreLocator',
    components: {
        GoogleMap
    },
    props: {
        apiKey: {
            type: String,
            required: true
        },
        mapId: {
            type: String,
            required: true
        },
        customerNumbers: {
            type: String,
            default: '',
            required: false
        }
    },
    setup() {
        const { formatStoreAddress, googleMapsUrl } = useStores();

        // Return the methods to make them available in the template
        return {
            formatStoreAddress,
            googleMapsUrl
        };
    },
    data() {
        return {
            mapInstance: null,
            mapBounds: null,
            isLoading: false,
            countries: [
                { code: 'AU', label: 'Australia' },
                { code: 'AT', label: 'Austria' },
                { code: 'BE', label: 'Belgium' },
                { code: 'BA', label: 'Bosnia and Herzegovina' },
                { code: 'BG', label: 'Bulgaria' },
                { code: 'CA', label: 'Canada' },
                { code: 'CN', label: 'China' },
                { code: 'HR', label: 'Croatia' },
                { code: 'CZ', label: 'Czech Republic' },
                { code: 'DK', label: 'Denmark' },
                { code: 'EE', label: 'Estonia' },
                { code: 'FO', label: 'Faroe Islands' },
                { code: 'FI', label: 'Finland' },
                { code: 'FR', label: 'France' },
                { code: 'DE', label: 'Germany' },
                { code: 'HK', label: 'Hong Kong' },
                { code: 'HU', label: 'Hungary' },
                { code: 'IN', label: 'India' },
                { code: 'ID', label: 'Indonesia' },
                { code: 'IE', label: 'Ireland' },
                { code: 'IT', label: 'Italy' },
                { code: 'JP', label: 'Japan' },
                { code: 'KW', label: 'Kuwait' },
                { code: 'LV', label: 'Latvia' },
                { code: 'LT', label: 'Lithuania' },
                { code: 'LU', label: 'Luxembourg' },
                { code: 'MX', label: 'Mexico' },
                { code: 'NL', label: 'Netherlands' },
                { code: 'NO', label: 'Norway' },
                { code: 'PL', label: 'Poland' },
                { code: 'PT', label: 'Portugal' },
                { code: 'RO', label: 'Romania' },
                { code: 'RS', label: 'Serbia' },
                { code: 'SK', label: 'Slovakia' },
                { code: 'SI', label: 'Slovenia' },
                { code: 'KR', label: 'South Korea' },
                { code: 'ES', label: 'Spain' },
                { code: 'SE', label: 'Sweden' },
                { code: 'CH', label: 'Switzerland' },
                { code: 'TR', label: 'Turkey' },
                { code: 'AE', label: 'United Arab Emirates' },
                { code: 'GB', label: 'United Kingdom' },
                { code: 'US', label: 'United States' }
            ],
            selectedCountry: '',
            searchTerm: '',
            debounceTimer: null,
            stores: [],
            mapCenter: null,
            errorMessage: ''
        };
    },
    computed: {
        filteredStores() {
            if (!this.isLoading) {
                const searchTermLower = this.searchTerm && this.searchTerm.toLowerCase();
                let result = this.stores
                    .filter(store => (
                        store.shopPostcode?.toLowerCase().includes(searchTermLower) ||
                        store.shopCity?.toLowerCase().includes(searchTermLower)
                    ));

                // If no result, try the first 3 digits of the postcode as a fallback
                if (result.length === 0) {
                    const partialZip = searchTermLower.substring(0, 3);
                    result = this.stores.filter(store =>
                        store.shopPostcode?.toLowerCase().startsWith(partialZip)
                    );
                }

                // Group stores by shopNo to avoid duplicates
                const groupedResult = Array.from(new Map(result.map(store => [store.shopNo, store])).values());

                return groupedResult
                    .sort((a, b) => a.shopName.localeCompare(b.shopName))
                    .map(store => ({
                        ...store,
                        position: { lat: store.latitude, lng: store.longitude }
                    }));
            }
            return [];
        },
        visibleStores() {
            if (!this.mapBounds) return this.filteredStores;
            return this.filteredStores.filter(store => {
                const position = new google.maps.LatLng(store.position.lat, store.position.lng);
                return this.mapBounds.contains(position);
            });
        }
    },
    created() {
        // Try geolocation → map to country
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async position => {
                    const { Geocoder } = await google.maps.importLibrary('geocoding');
                    const geocoder = new Geocoder();

                    let latlng = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };

                    geocoder.geocode({ location: latlng }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const countryComponent = results[0].address_components.find(c =>
                                c.types.includes('country')
                            );

                            if (countryComponent) {
                                const countryCode = countryComponent.short_name;

                                if (countryCode && countryCode !== this.selectedCountry && this.countries.some(c => c.code === countryCode)) {
                                    this.selectedCountry = countryCode;
                                } else {
                                    latlng = null;
                                }
                            } else {
                                latlng = null;
                            }
                        } else {
                            console.warn('Geocoder failed:', status);
                            latlng = null;
                        }

                        // Set map center to geolocation
                        this.mapCenter = latlng;

                        // Always attempt to fetch stores for whatever selectedCountry is
                        this.getStores();

                    });
                },
                error => {
                    console.warn('Geolocation failed or denied:', error.message);
                }
            );
        }
    },
    methods: {
        onMapReady(map) {
            this.mapInstance = map;
            this.mapBounds = map.getBounds();

            map.addListener('bounds_changed', () => {
                this.mapBounds = map.getBounds();
            });
        },
        countryUpdated() {
            this.getStores();
        },
        searchTermUpdated(value) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.searchTerm = value;
            }, 300);
        },
        getStores() {
            const vm = this;

            vm.errorMessage = '';

            if (this.selectedCountry) {
                vm.isLoading = true;

                apiService.store.getStores(this.selectedCountry, this.customerNumbers)
                    .then(response => {
                        vm.stores = response.data;
                    })
                    .catch(errors => {
                        const errorResponse = errors.response.data.errorMessage ? errors.response.data.errorMessage : `Error (status code ${errors.response.status})`;
                        vm.errorMessage = errorResponse;
                    })
                    .then(function () {
                        vm.isLoading = false;
                    });
            } else {
                vm.stores = [];
            }
        }
    }
};
</script>
